/**
 * Ids for the chores a household defines itself.
 *
 * A task id is permanent in the strongest sense this app has: it is the middle
 * component of every completion id ever recorded against the chore, and that id
 * is half of the primary key of the row, locally and remotely. Renaming one
 * orphans every past checkmark — `CLAUDE.md` §4. So an id is minted once, here,
 * and never derived from anything the user can edit: a slug built from the
 * label would change the moment somebody fixed a typo.
 *
 * The built-in chores keep their editorial slugs (`mon-robot`), as
 * `docs/architecture-review.md` §1.3 says they should while the plan is code.
 * The `custom-` prefix is what keeps the two namespaces from ever meeting.
 */

export const CUSTOM_TASK_PREFIX = 'custom-'

/**
 * A fresh id: the prefix, the minute it was created, and some noise.
 *
 * The timestamp is there to be read by a human looking at a table, not for
 * ordering — nothing sorts on it. The random tail is what makes two devices
 * offline at the same moment produce two chores rather than one collision, and
 * a collision here is worse than a duplicate row: the two chores would share
 * every checkmark.
 */
export function newTaskId(): string {
  const stamp = Date.now().toString(36)
  const noise = Math.random().toString(36).slice(2, 10).padEnd(8, '0')
  return `${CUSTOM_TASK_PREFIX}${stamp}-${noise}`
}

/**
 * Whether an id can safely be put through `encodeCompletionId`.
 *
 * `encodeCompletionId` *throws* on a colon, by design, because everything it is
 * given is supposed to be a literal or a minted id. Once chores are data that
 * stops being true for one path: a row could arrive from the server, or out of
 * a hand-edited `localStorage`, carrying a colon in its id — and the throw
 * would then happen inside the day list's render and take the whole page down.
 *
 * `buildPlan` filters on this so such a row is merely invisible, which is the
 * same trade `decodeCompletionId` makes for the same reason: one unreadable
 * chore must not stop the month view rendering the other four hundred.
 */
export function isUsableTaskId(id: string): boolean {
  return id !== '' && !id.includes(':')
}
